import {
  EnrichedBinding,
  Snippet,
  ContextUser,
  SummariseLength,
} from "@budibase/types"
import { LLMRequest } from "../llm"
import { ChatCompletionContentPart } from "openai/resources/chat/completions"
import { z } from "zod"

export function summarizeText(text: string, length?: SummariseLength) {
  const basePrompt = `Summarize this text:\n${text}`
  let lengthPrompt = ""
  if (length) {
    switch (length) {
      case SummariseLength.SHORT:
        lengthPrompt = "In 1-2 concise sentences, "
        break
      case SummariseLength.MEDIUM:
        lengthPrompt = "In 2-3 paragraphs, "
        break
      case SummariseLength.LONG:
        lengthPrompt = "In multiple detailed paragraphs, "
        break
    }
  }
  return new LLMRequest().addUserMessage(
    `${lengthPrompt}${basePrompt}. \nOnly return the summary.`
  )
}

export function extractFileData(
  schema: Record<string, any>,
  fileIdOrDataUrl: string
) {
  const prompt = [
    "You are a data extraction assistant.",
    `Extract data from the attached document/image that matches the provided schema.`,
    "The schema defines the structure where values like 'string', 'number', 'boolean' indicate the expected data types.",
    "Extract all items that match the schema from the document.",
    "Return the data in json format",
    "If no matching data is found, return an empty data array.",
  ].join("\n\n")

  // Check if it's a base64 data URL (for images) or a file ID (for documents)
  const isDataUrl = fileIdOrDataUrl.startsWith("data:")

  const content: ChatCompletionContentPart[] = isDataUrl
    ? [
        {
          type: "image_url",
          image_url: {
            url: fileIdOrDataUrl,
          },
        },
        { type: "text", text: prompt },
      ]
    : [{ type: "text", text: `${prompt}\n\nFile ID: ${fileIdOrDataUrl}` }]

  // We create a structured zod schema from the user object
  const zodSchema = createZodSchemaFromRecord(schema)
  const responseSchema = z.object({
    data: z.array(zodSchema),
  })

  return new LLMRequest()
    .addMessages([
      {
        role: "user",
        content,
      },
    ])
    .withFormat(responseSchema)
}

function createZodSchemaFromRecord(
  schema: Record<string, any>
): z.ZodType<any> {
  const zodFields: Record<string, z.ZodType<any>> = {}

  for (const [key, type] of Object.entries(schema)) {
    if (typeof type === "string") {
      switch (type.toLowerCase()) {
        case "string":
          zodFields[key] = z.string()
          break
        case "number":
          zodFields[key] = z.number()
          break
        case "boolean":
          zodFields[key] = z.boolean()
          break
        default:
          zodFields[key] = z.string()
      }
    } else {
      zodFields[key] = z.string()
    }
  }

  return z.object(zodFields)
}
export function classifyText(text: string, categories: string[]) {
  return new LLMRequest().addUserMessage(
    `Return the category of this text: "${text}". Based on these categories: ${categories.join(
      ", "
    )}. Only return the category. Do not add any extra punctuation or fix spelling mistakes. It should exactly match one of the categories. `
  )
}

export function cleanData(text: string) {
  return new LLMRequest().addUserMessage(
    `Clean the following string: "${text}". Remove any spelling mistakes or data issues. Only return the cleaned string itself and nothing else.`
  )
}

export function generateSQL(prompt: string, tableSchema: string) {
  return new LLMRequest().addUserMessage(
    `Given the table schema:\n${tableSchema}\n\nGenerate a SQL query for the following request:\n${prompt}.\n Only provide the SQL.`
  )
}

export function generateCode(prompt: string) {
  return new LLMRequest().addUserMessage(
    `Generate JavaScript code for the following request:\n${prompt}.\n Only provide the JS and nothing else.`
  )
}

export function generateCronExpression(text: string) {
  return new LLMRequest().addUserMessage(
    `Generate a node-cron compatible expression based on the following prompt. Return only the cron expression (without backticks), and if not possible return only 'Error generating cron' with a short explanation:\n${text}`
  )
}

export function translate(text: string, language: string) {
  return new LLMRequest().addUserMessage(
    `Translate the following text: "${text}" into ${language}. Only return the translation.`
  )
}

export function sentimentAnalysis(text: string) {
  return new LLMRequest().addUserMessage(
    `Return the sentiment of this text: "${text}". Only return the sentiment.`
  )
}

export function searchWeb(text: string) {
  return new LLMRequest().addUserMessage(
    `Search the web for the following: "${text}". Only return the top results.`
  )
}

export function generateJs(bindings: EnrichedBinding[], snippets: Snippet[]) {
  let bindingsPrompt = "You do not have access to any bindings in this request."
  if (bindings && bindings.length > 0) {
    bindingsPrompt = `The bindings you have access to are: \n\n${bindings
      .map(({ readableBinding }) => `- $("${readableBinding}")`)
      .join("\n")}`
  }

  let snippetsPrompt = "You do not have access to any snippets in this request."
  if (snippets.length > 0) {
    snippetsPrompt = `The snippets you have access to are: \n\n${snippets
      .map(({ name, code }) => {
        const codeWithoutReturn = code.replace(/^\s*return /g, "")
        return `- snippets.${name} = ${codeWithoutReturn.trim()}`
      })
      .join("\n")}`
  }

  return new LLMRequest().addSystemMessage(`
You are a helpful expert in writing JavaScript.  A user is asking you for help
writing some JavaScript for their application.

Your reply MUST only contain JavaScript code. No explanations,
no markdown, no delimiters around it. It is crucial for it to only
be the code itself.

The JavaScript you write is going to be run in a sandboxed environment. It
only has access to the things I describe to you in this description. It does
not have access to things like \`fetch\` and \`window\`. It also does not have
access to the global scope. You can only use the variables and functions I
describe to you.

The code you are to return is a JavaScript function, except without
the function signature, opening brace, and closing brace. Just the
content of the function. The wrapper around the function is
generated elsewhere. Your code _must_ complete with a \`return\` statement.
It is incorrect for there to be no returns, or for the return to be
missing a value.

If you receive a request that is not to generate code, is abusive, is
inappropriate, is vague, is ambiguous, or is otherwise not useful, you should
respond with an empty string. Nothing else, just a completely blank response.
Your code is to write code and you should not attempt to do anything else.

The JavaScript code you're going to write has access to some special global
variables and functions that are not standard to JavaScript. These are:

- \`$\`: This function allows you to access a context object. The contents of
  this object will be described later. It takes a string and can return data
  of any primitive JS type. You are to infer the type and meaning of the data
  from the string you pass to it. An example of how to call this function would
  be \`$("Current User.firstName")\`. There are no other properties on \`$\`,
  do not try to access them.
- \`snippets\`: An object that contains references to many user-defined utility
  functions. A full list will be provided later. If a solution is possible
  using the provided snippets, you should use them. All calls to snippet
  functions look like \`snippets.myFunctionName(args)\`.

What follows are some example responses, indented 2 spaces, with an explanation
beneath each of whether it's right or wrong.

  console.log("Hello, world!")

This is incorrect but only because there is no return statement. All code you
produce must return a value.

  const foo = "bar"
  return foo

This is correct.

  const length = $("Current User.firstName").length
  return length

This is correct, and correctly uses a binding.

  const name = $.get("Current User.firstName")
  return name

This is incorrect, $.get is not a function.

  return window.location.href;

This is incorrect, window is not available in our sandbox.

  function main() {
    return 4;
  }
  main();

This is incorrect, nothing is returned. It's fine to define functions but the
code you produce must return a value.

  return someVar.length;

This is incorrect because it references a variable that is not defined. When you
are asked to do something with some value, that value will be provided to you
as a binding.

  return snippets.processForAccount(amount);

This is incorrect because it references an undefined variable "amount". It is
important that you never reference a variable that is not defined. If you need
a variable, it will be provided to you as a binding.

${snippetsPrompt}

${bindingsPrompt}`)
}

export function generateTables() {
  const tablesMessage = `
You are generating Budibase table schemas from user prompts.
Always return at least 2 tables, and define only one side of relationships using a link field.
Exclude id, created_at, and updated_at (Budibase adds them).
Include a variety of column types: text, dropdown, date, number.
Add at least one formula column, one attachment, and one multi-attachment column across the tables.
Budibase handles reverse relationships and many-to-many links — never define join tables or reverse fields.
You may specify foreignColumnName, but do not create that field manually.
`

  return new LLMRequest().addSystemMessage(tablesMessage)
}

export function generateAIColumns() {
  const tablesMessage = `Given the generated schema, add only one field of type "AI" to relevant tables to add value to the Budibase user.`

  return new LLMRequest().addSystemMessage(tablesMessage)
}

export function generateData() {
  const dataMessage = `
For each table, populate the data field with realistic-looking sample records.
Avoid placeholder values like "foo" or "bar". Use real names, emails, etc., and ensure values are unique across rows.
`

  return new LLMRequest().addSystemMessage(dataMessage)
}

export function agentSystemPrompt(user: ContextUser) {
  return `You are a helpful support agent who uses workflows to resolve user issues efficiently.

  - The user will ask support queries.
  - Your replies MUST be short, concise, and directly answer the user's question as quickly and clearly as possible.
  - Use Markdown formatting in your responses.
  - When receiving truncated or paginated results, automatically make follow-up requests to retrieve all pages
  - If you aren't entirely sure which tool to call, make sure to ask for confirmation rather than assume. If there's any ambiguity, get user confirmation.
  - When a tool call fails, show the detailed error status and message in the UI to provide the user further information as to how to debug.
  - When specifying a "limit" for a certain tool call related to the number of records, use at least 100. This helps prevent cutting off the list of results too short. If the number of results overflows the limit, make sure you tell the user there are more, and confirm if they want to fetch the rest before continuing.


  User information is provided below for context:
  
  \`\`\`
  ${JSON.stringify(user)}
  \`\`\``
}

export function agentHistoryTitleSystemPrompt() {
  return `You will be provided with a set of history, a conversation between a user and an AI. Using this
  information generate a title for the conversation, this should start with a capital letter and MUST be 
  less than five words.`
}
